import { Directive, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from "@angular/core";

import { CoachMarkService } from "@app/services/coach-mark.service";

/**
 * Tags an element as an anchor the coach-mark engine (FLIP-303) can target by key, e.g.
 * `<div [flipCoachTarget]="'custom-card'">`. Also stamps a `data-flip-coach-target` attribute,
 * so `CoachMarkService` can resolve a target even if the directive instance itself has already
 * been destroyed and re-created (list virtualization, *ngFor re-renders, etc.).
 *
 * The key may be bound conditionally (e.g. `[flipCoachTarget]="card.isUserDefined ? 'custom-card' : null"`
 * inside an `*ngFor`, so only the matching element(s) register) - a falsy key is a no-op rather
 * than an error, since the directive still runs for every row.
 *
 * A key that changes after init re-registers (FLIP-340): one element can serve different anchors
 * over its lifetime, e.g. the scoring session's add button is Add Star or Add Shadow by tab.
 */
@Directive({
  selector: "[flipCoachTarget]",
  standalone: true,
})
export class FlipCoachTargetDirective implements OnInit, OnChanges, OnDestroy {
  @Input("flipCoachTarget") key: string | null | undefined;

  private registeredKey: string | null = null;

  constructor(
    private readonly el: ElementRef<HTMLElement>,
    private readonly coachMarkService: CoachMarkService
  ) {}

  ngOnInit(): void {
    this.register();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // The first binding is handled by ngOnInit.
    if (!changes["key"] || changes["key"].firstChange) return;
    this.deregister();
    this.register();
  }

  ngOnDestroy(): void {
    this.deregister();
  }

  private register(): void {
    if (!this.key) return;
    this.el.nativeElement.setAttribute("data-flip-coach-target", this.key);
    this.coachMarkService.registerTarget(this.key, this.el.nativeElement);
    this.registeredKey = this.key;
  }

  private deregister(): void {
    if (!this.registeredKey) return;
    this.el.nativeElement.removeAttribute("data-flip-coach-target");
    this.coachMarkService.deregisterTarget(this.registeredKey, this.el.nativeElement);
    this.registeredKey = null;
  }
}
